import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import { Button } from '../components/ui/button';
import { ServiceComparison } from '../components/ServiceComparison';
import { SALES_ADVISORY as C } from '../data/salesAdvisoryData';
import { breadcrumbSchema, webPageSchema, serviceSchema } from '../lib/seo';

const crumbs = [{ name: 'Home', path: '/' }, { name: 'Sales Advisory', path: C.seo.path }];

const CtaButton = ({ text, to, testid, light }) => (
  <Button asChild data-testid={testid} className={`${light ? 'bg-[#EBE4DE] text-[#2E2C28] hover:bg-[#B5876C] hover:text-[#EBE4DE]' : 'bg-[#2E2C28] text-[#EBE4DE] hover:bg-[#B5876C]'} rounded-none h-14 px-8 text-[12px] tracking-[0.2em] uppercase transition-colors duration-500 press`}>
    <Link to={to} className="inline-flex items-center gap-3">{text} <ArrowUpRight size={16} strokeWidth={1.5} /></Link>
  </Button>
);

const H2 = ({ children }) => (
  <h2 className="font-serif text-[32px] sm:text-[38px] lg:text-[46px] leading-[1.08] tracking-[-0.01em] text-[#2E2C28] font-light text-balance">{children}</h2>
);

const Section = ({ num, label, testid, children }) => (
  <section data-reveal data-testid={testid} className="py-20 lg:py-28 border-t border-[#d9d2ca] grid grid-cols-1 lg:grid-cols-12 gap-y-6 lg:gap-x-8">
    <div className="lg:col-span-4">
      <p className="sec-num text-[13px] tracking-[0.28em] uppercase lg:pt-4">{num} / {label}</p>
    </div>
    <div className="lg:col-span-8 xl:col-span-7">{children}</div>
  </section>
);

const Hero = () => (
  <header className="pt-10 pb-20 lg:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-y-6 lg:gap-x-8" data-testid="sa-hero">
    <div className="lg:col-span-4"><p className="sec-num text-[13px] tracking-[0.28em] uppercase lg:pt-6">/ Advisory</p></div>
    <div className="lg:col-span-8 xl:col-span-7">
      <h1 data-testid="sa-hero-title" className="font-serif text-[48px] sm:text-[64px] lg:text-[84px] leading-[0.98] tracking-[-0.02em] text-[#2E2C28] font-light">{C.hero.title}</h1>
      <p className="mt-7 font-serif italic text-[22px] sm:text-[26px] lg:text-[30px] leading-[1.25] text-[#B5876C] font-light text-balance">{C.hero.subtitle}</p>
      <div className="mt-10 pt-10 border-t border-[#d9d2ca] max-w-[36rem]">
        <p className="text-[17px] lg:text-lg leading-[1.75] text-[#4a463f] font-light text-pretty">{C.hero.body}</p>
        <div className="mt-10"><CtaButton text={C.hero.ctaText} to={C.hero.ctaLink} testid="sa-hero-cta" /></div>
      </div>
    </div>
  </header>
);

const WhoFor = () => (
  <Section num="01" label="Audience" testid="sa-who-for">
    <H2>{C.whoFor.heading}</H2>
    <p className="mt-8 font-serif text-[22px] lg:text-[26px] leading-[1.35] text-[#2E2C28] font-light max-w-[34rem] text-pretty">{C.whoFor.lead}</p>
    <p className="mt-6 text-[17px] leading-[1.75] text-[#4a463f] font-light max-w-[36rem] text-pretty">{C.whoFor.body}</p>
  </Section>
);

const Monthly = () => (
  <Section num="02" label="Included" testid="sa-monthly">
    <H2>{C.monthly.heading}</H2>
    <div className="mt-12 lg:mt-14 grid grid-cols-1 sm:grid-cols-2 gap-x-12 gap-y-12">
      {C.monthly.items.map((it, i) => (
        <article key={it.title} data-testid={`sa-monthly-card-${i}`} className="group border-t border-[#2E2C28]/20 pt-7">
          <div className="flex items-baseline gap-4">
            <span className="sec-num text-[15px] w-6 shrink-0">0{i + 1}</span>
            <h3 className="font-serif text-[22px] lg:text-[24px] leading-tight text-[#2E2C28] font-light transition-colors duration-500 group-hover:text-[#B5876C]">{it.title}</h3>
          </div>
          <p className="mt-4 pl-10 text-base leading-[1.7] text-[#4a463f] font-light text-pretty">{it.body}</p>
        </article>
      ))}
    </div>
  </Section>
);

const NotIncluded = () => (
  <Section num="03" label="Boundaries" testid="sa-not-included">
    <H2>{C.notIncluded.heading}</H2>
    <p className="mt-6 font-serif italic text-[19px] lg:text-[21px] leading-snug text-[#B5876C] font-light">{C.notIncluded.lead}</p>
    <ul className="mt-8 space-y-4 max-w-[36rem]">
      {C.notIncluded.items.map((t, i) => (
        <li key={i} className="grid grid-cols-[1.5rem_1fr] text-base leading-[1.7] text-[#4a463f] font-light text-pretty">
          <span aria-hidden="true" className="text-[#B5876C]">&ndash;</span>{t}
        </li>
      ))}
    </ul>
  </Section>
);

const Term = ({ label, children, testid }) => (
  <div data-testid={testid} className="grid grid-cols-1 sm:grid-cols-[10rem_1fr] gap-y-3 gap-x-8 py-8 border-b border-[#d9d2ca]">
    <dt className="text-[12px] tracking-[0.24em] uppercase text-[#8a8378] sm:pt-1.5">{label}</dt>
    <dd>{children}</dd>
  </div>
);

const HowItWorks = () => {
  const h = C.howItWorks;
  const body = 'text-[17px] leading-[1.7] text-[#4a463f] font-light max-w-[30rem] text-pretty';
  return (
    <Section num="04" label="Terms" testid="sa-how-it-works">
      <H2>{h.heading}</H2>
      <dl className="mt-10 border-t border-[#2E2C28]/20">
        <Term label={h.investment.label} testid="sa-investment">
          <p className="flex items-baseline gap-3">
            <span data-testid="sa-price" className="font-serif text-[44px] lg:text-[52px] leading-none tracking-[-0.01em] text-[#2E2C28] font-light">{h.investment.price}</span>
            <span className="font-serif italic text-lg text-[#B5876C]">{h.investment.period}.</span>
          </p>
        </Term>
        <Term label={h.commitment.label}><p className={body}>{h.commitment.body}</p></Term>
        <Term label={h.movingUp.label}><p className={body}>{h.movingUp.body}</p></Term>
      </dl>
    </Section>
  );
};

const FinalCta = () => (
  <section data-reveal data-testid="sa-final-cta" className="bg-[#2E2C28] text-[#EBE4DE] px-8 py-16 sm:px-12 lg:px-16 lg:py-20 grid grid-cols-1 lg:grid-cols-12 gap-y-8 lg:gap-x-8 items-end">
    <div className="lg:col-span-8">
      <h2 className="font-serif text-[32px] sm:text-[38px] lg:text-[46px] leading-[1.08] tracking-[-0.01em] font-light text-balance">{C.finalCta.heading}</h2>
      <p className="mt-5 text-[17px] leading-[1.7] text-[#EBE4DE]/75 font-light">{C.finalCta.body}</p>
    </div>
    <div className="lg:col-span-4 lg:justify-self-end">
      <CtaButton text={C.finalCta.ctaText} to={C.finalCta.ctaLink} testid="sa-final-cta-btn" light />
    </div>
  </section>
);

export default function SalesAdvisoryPage() {
  const { path, title, description } = C.seo;
  const schemas = [
    webPageSchema({ path, name: title, description }),
    breadcrumbSchema(crumbs),
    serviceSchema({ name: C.hero.title, description, path }),
  ];
  return (
    <div className="bg-[#EBE4DE] text-[#2E2C28] min-h-screen" data-testid="sales-advisory-page">
      <SEO title={title} description={description} path={path} schemas={schemas} />
      <Navbar />
      <main className="pt-36 pb-20 lg:pb-28">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <Breadcrumbs items={crumbs} />
          <Hero />
          <WhoFor />
          <Monthly />
          <NotIncluded />
          <section className="py-20 lg:py-28 border-t border-[#d9d2ca]"><ServiceComparison current="advisory" /></section>
          <HowItWorks />
          <FinalCta />
        </div>
      </main>
      <Footer />
    </div>
  );
}
